/**
 * Environment Configuration
 *
 * Single source of truth for all environment-dependent values.
 * Reads from REACT_APP_* env vars injected at build time by CRA.
 *
 * Google Maps API Key Setup
 * -------------------------
 * 1. Enable these APIs in Google Cloud Console:
 *    - Maps JavaScript API
 *    - Places API
 *    - Geocoding API
 * 2. Restrict the key to your allowed domains under API Credentials.
 * 3. Link a billing account (required for Google Maps APIs).
 * 4. Set the key in your .env.local or the appropriate .env.{env} file.
 */

type Environment = 'development' | 'staging' | 'production';

const env = (key: string, fallback: string = ''): string =>
  process.env[key] ?? fallback;

const envBool = (key: string, fallback: boolean = false): boolean => {
  const val = process.env[key];
  if (val === undefined) return fallback;
  return val === 'true' || val === '1';
};

const envNumber = (key: string, fallback: number): number => {
  const val = process.env[key];
  if (val === undefined) return fallback;
  const parsed = parseInt(val, 10);
  return isNaN(parsed) ? fallback : parsed;
};

export const environment: Environment =
  (env('REACT_APP_ENVIRONMENT', 'development') as Environment);

export const config = {
  // App
  appName: env('REACT_APP_NAME', 'UB Home Health'),
  appVersion: env('REACT_APP_VERSION', '2.0.0'),
  environment,

  // API
  apiBaseUrl: env('REACT_APP_API_BASE_URL', 'https://hhapi.app.uborahealth.info'),
  apiTimeout: envNumber('REACT_APP_API_TIMEOUT', 30000),

  // Feature Flags
  enableDevtools: envBool('REACT_APP_ENABLE_DEVTOOLS', environment === 'development'),
  enableLogging: envBool('REACT_APP_ENABLE_LOGGING', environment !== 'production'),
  enableAnalytics: envBool('REACT_APP_ENABLE_ANALYTICS', environment === 'production'),

  // Auth Storage Keys
  authTokenKey: env('REACT_APP_AUTH_TOKEN_KEY', 'ub_hh_auth_token'),
  authRefreshTokenKey: env('REACT_APP_AUTH_REFRESH_TOKEN_KEY', 'ub_hh_refresh_token'),
  themeStorageKey: env('REACT_APP_THEME_STORAGE_KEY', 'ub_hh_theme'),

  // External Services
  googleMapsApiKey: env('REACT_APP_GOOGLE_MAPS_API_KEY', ''),

  // Optional
  defaultClientId: env('REACT_APP_DEFAULT_CLIENT_ID', ''),
} as const;

export const isDev = environment === 'development';
export const isStaging = environment === 'staging';
export const isProd = environment === 'production';

export default config;
